import styles from './SalonInfo.module.scss';
import { useDispatch, useSelector } from 'react-redux';
import { getActiveSalon } from '../../store/selectors';
import React, { useMemo } from 'react';
import TitleSectionBack from '../../../Common/TitleSectionBack';
import { Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import { modalActions } from '../../../Modals/store';
import AvatarIcon from '../../../Common/AvatarIcon';
import TextField from '../../../Common/TextField';
import HoveredInfoIcon from '../../../Common/HoveredInfoIcon';
import Switcher from '../../../Common/Switcher';
import ConfirmSecondaryModal from '../../../Common/ConfirmSecondaryModal';
import { authActions } from '../../store';
import useWindowSize from '../../../../utils/useWindowSize';
import clx from 'classnames';
import ButtonSelector from 'Containers/Common/CustomSelector/ButtonSelector';
import { getActiveSalonePaymentInfo } from 'utils/salonToken';
import { useTranslation } from 'react-i18next';
import { Camera, ImagePlus, Pencil } from 'lucide-react';

const SalonInfo = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const salon = useSelector(getActiveSalon());
  const { isMobile } = useWindowSize();
  const paymentDisabled = getActiveSalonePaymentInfo();
  const isHomeStatsHidden = salon?.hideHomeStats === 1 || salon?.hideHomeStats === true;
  const [isResetConfirmOpen, setIsResetConfirmOpen] = React.useState(false);

  const bookableDayOptions = useMemo(
    () => [
      { name: '7', value: 7 },
      { name: '15', value: 15 },
      { name: '21', value: 21 },
      { name: '30', value: 30 },
      { name: '60', value: 60 },
      { name: t('salonInfo.block'), value: 0 },
    ],
    [t],
  );

  const visibleInPastOptions = useMemo(
    () => [
      { name: '1', value: 1 },
      { name: '7', value: 7 },
      { name: '15', value: 15 },
      { name: '30', value: 30 },
    ],
    [],
  );

  const nMaxPrenotazioniOptions = useMemo(
    () => [
      { name: '1', value: 1 },
      { name: '3', value: 3 },
      { name: '5', value: 5 },
      { name: '10', value: 10 },
    ],
    [],
  );

  const initialValues = {
    nomeSalone: salon.name || '',
    tel: salon.phoneNumber || '',
    nMaxPrenotazioni: +salon.numberOfAppointments || 0,
    n_giorni_prenotabili: +salon.numberOfBookableDays || 0,
    n_giorni_passati: +salon.giorniVisibiliSalonePassato || 0,
    isOldStyle: +salon.isOldStyle,
    serviceCombos: 0,
    email: salon.email || '',
    coverImg: '',
    avatarImg: '',
    new_user_must_pay: salon.new_user_must_pay,
  };

  const validationSchema = {
    nomeSalone: yup
      .string(t('auth.validation.enterName'))
      .required(t('auth.validation.nameRequired')),
    nMaxPrenotazioni: yup
      .number()
      .typeError(t('auth.validation.enterMaxAppointments')),
    n_giorni_prenotabili: yup
      .number()
      .typeError(t('auth.validation.enterMaxBookableDays')),
    n_giorni_passati: yup
      .number()
      .typeError(t('auth.validation.enterMaxPastDays')),
  };

  const openImageModal = (type, setFieldValue, field) => {
    dispatch(
      modalActions.OPEN_MODAL.SUCCESS({
        type,
        callback: (value) => setFieldValue(field, value),
      }),
    );
  };

  return (
    <div className={clx(styles.salon_container, { [styles.salon_container_mobile]: isMobile })}>
      <div className={styles.header}>
        <TitleSectionBack
          title={t('salonInfo.title')}
          description={t('salonInfo.description')}
        />
      </div>
      <Formik
        initialValues={initialValues}
        validationSchema={yup.object().shape(validationSchema)}
        enableReinitialize={true}
        onSubmit={(values) => {
          const { coverImg, avatarImg, ...req } = values;
          dispatch(authActions.EDIT_SALON_INFO.REQUEST(req));
        }}
      >
        {({ values, setFieldValue }) => {
          const coverSrc = values.coverImg || salon.coverImg;
          return (
            <Form className={styles.salon_card}>
              <div className={styles.image_block}>
                <div className={styles.section_label}>
                  {t('salonInfo.coverAndProfileImage')}
                  <HoveredInfoIcon description={t('salonInfo.coverAndProfileImageDescription')} />
                </div>
                <div
                  className={clx(styles.cover_preview, {
                    [styles.cover_preview_empty]: !coverSrc,
                  })}
                  style={coverSrc ? { backgroundImage: `url(${coverSrc})` } : undefined}
                >
                  {!coverSrc && (
                    <div className={styles.cover_placeholder}>
                      <ImagePlus size={22} strokeWidth={2.2} />
                      <span>{t('salonInfo.noCover')}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className={clx(styles.edit_btn, styles.cover_edit)}
                    aria-label={t('salonInfo.editCover')}
                    onClick={() =>
                      openImageModal('createSalonCoverModal', setFieldValue, 'coverImg')
                    }
                  >
                    <Camera size={16} strokeWidth={2.2} />
                  </button>
                  <div className={styles.avatar_wrap}>
                    <AvatarIcon
                      name={salon.name}
                      icon={values.avatarImg || salon.icon}
                      size={isMobile ? '72px' : '88px'}
                    />
                    <button
                      type="button"
                      className={clx(styles.edit_btn, styles.avatar_edit)}
                      aria-label={t('salonInfo.editAvatar')}
                      onClick={() =>
                        openImageModal('createSalonAvatarModal', setFieldValue, 'avatarImg')
                      }
                    >
                      <Pencil size={14} strokeWidth={2.2} />
                    </button>
                  </div>
                </div>
              </div>

              <div className={styles.form_body}>
                <Field
                  name={'nomeSalone'}
                  id={'nomeSalone'}
                  label={t('salonInfo.salonName')}
                  component={TextField}
                  height={'50px'}
                  margin={'0px 0px 16px 0px'}
                  placeholder={t('salonInfo.textFieldPlaceholder')}
                />
                <Field
                  name={'email'}
                  id={'email'}
                  label={t('salonInfo.email')}
                  component={TextField}
                  height={'50px'}
                  placeholder={t('salonInfo.demoEmail')}
                  margin={'0px 0px 16px 0px'}
                  disabled={true}
                />
                <Field
                  name={'tel'}
                  id={'tel'}
                  label={t('salonInfo.phoneNumber')}
                  component={TextField}
                  height={'50px'}
                  margin={'0px 0px 8px 0px'}
                  placeholder={t('salonInfo.phonePlaceholder')}
                />

                <div className={styles.setting_row}>
                  <div className={styles.setting_copy}>
                    <span className={styles.setting_title}>{t('salonInfo.maxAppointments')}</span>
                    <HoveredInfoIcon description={t('salonInfo.maxAppointmentsDescription')} />
                  </div>
                  <div className={styles.setting_control}>
                    <ButtonSelector
                      name={'nMaxPrenotazioni'}
                      id={'nMaxPrenotazioni'}
                      options={
                        nMaxPrenotazioniOptions.find((p) => p.value === values.nMaxPrenotazioni)
                          ? nMaxPrenotazioniOptions
                          : [
                              ...nMaxPrenotazioniOptions,
                              {
                                name: `${values.nMaxPrenotazioni}`,
                                value: values.nMaxPrenotazioni,
                              },
                            ]
                      }
                      value={values.nMaxPrenotazioni}
                      setValue={(value) => {
                        setFieldValue('nMaxPrenotazioni', value);
                      }}
                      marginLeft="0px"
                      width="100%"
                    />
                  </div>
                </div>

                <div className={styles.setting_row}>
                  <div className={styles.setting_copy}>
                    <span className={styles.setting_title}>{t('salonInfo.maxBookableDays')}</span>
                    <HoveredInfoIcon description={t('salonInfo.maxBookableDaysDescription')} />
                  </div>
                  <div className={styles.setting_control}>
                    <ButtonSelector
                      name={'n_giorni_prenotabili'}
                      id={'n_giorni_prenotabili'}
                      options={
                        bookableDayOptions.find((p) => p.value === values.n_giorni_prenotabili)
                          ? bookableDayOptions
                          : [
                              ...bookableDayOptions,
                              {
                                name: `${values.n_giorni_prenotabili}`,
                                value: values.n_giorni_prenotabili,
                              },
                            ]
                      }
                      value={values.n_giorni_prenotabili}
                      setValue={(value) => {
                        setFieldValue('n_giorni_prenotabili', value);
                      }}
                      marginLeft="0px"
                      width="100%"
                    />
                  </div>
                </div>

                <div className={styles.setting_row}>
                  <div className={styles.setting_copy}>
                    <span className={styles.setting_title}>{t('salonInfo.maxPastDays')}</span>
                    <HoveredInfoIcon description={t('salonInfo.maxPastDaysDescription')} />
                  </div>
                  <div className={styles.setting_control}>
                    <ButtonSelector
                      name={'n_giorni_passati'}
                      id={'n_giorni_passati'}
                      options={
                        visibleInPastOptions.find((p) => p.value === values.n_giorni_passati)
                          ? visibleInPastOptions
                          : [
                              ...visibleInPastOptions,
                              {
                                name: `${values.n_giorni_passati}`,
                                value: values.n_giorni_passati,
                              },
                            ]
                      }
                      value={values.n_giorni_passati}
                      setValue={(value) => {
                        setFieldValue('n_giorni_passati', value);
                      }}
                      marginLeft="0px"
                      width="100%"
                    />
                  </div>
                </div>

                <div className={styles.setting_row}>
                  <div className={styles.setting_copy}>
                    <span className={styles.setting_title}>
                      {t('salonInfo.activateServiceCombos')}
                    </span>
                    <HoveredInfoIcon
                      description={t('salonInfo.activateServiceCombosDescription')}
                    />
                  </div>
                  <div className={styles.switcher_container}>
                    <Switcher
                      value={values.isOldStyle}
                      setValue={(value) => setFieldValue('isOldStyle', value ? 0 : 1)}
                      isFullWidth={isMobile}
                    />
                  </div>
                </div>

                {!paymentDisabled && (
                  <div className={styles.setting_row}>
                    <div className={styles.setting_copy}>
                      <span className={styles.setting_title}>
                        {t('salonInfo.onlinePaymentForNewClients')}
                      </span>
                      <HoveredInfoIcon
                        description={t('salonInfo.onlinePaymentForNewClientsDescription')}
                      />
                    </div>
                    <div className={styles.switcher_container}>
                      <Switcher
                        value={!values.new_user_must_pay}
                        setValue={(value) => setFieldValue('new_user_must_pay', +value)}
                        isFullWidth={isMobile}
                      />
                    </div>
                  </div>
                )}

                <div className={styles.actions}>
                  <button type="submit" className={styles.confirm_button}>
                    {t('common.actions.save')}
                  </button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>

      {!isHomeStatsHidden && (
        <div className={styles.reset_wrap}>
          <button
            type="button"
            className={styles.reset_link}
            onClick={() => setIsResetConfirmOpen(true)}
          >
            {t('salonInfo.resetToDefault')}
          </button>
          <HoveredInfoIcon
            description={t('salonInfo.resetHint')}
            sidePosition="right"
          />
        </div>
      )}

      <ConfirmSecondaryModal
        isModalOpen={isResetConfirmOpen}
        setIsModalOpen={setIsResetConfirmOpen}
        title={t('salonInfo.resetConfirmTitle')}
        subtitle={t('salonInfo.resetConfirmSubtitle')}
        buttons={[t('common.actions.confirm')]}
        buttonsActions={[
          () => {
            dispatch(authActions.RESET_HOME_STATS.REQUEST({ hideHomeStats: 1 }));
            setIsResetConfirmOpen(false);
          },
        ]}
      />
    </div>
  );
};

export default SalonInfo;
